import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.min";
import { Navigation } from "./Navigation";
import JsonData from "../data/data.json";

/* ✅ PDF.js worker */
pdfjsLib.GlobalWorkerOptions.workerSrc =
  `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export default function GuideView() {
  const containerRef = useRef(null);

  const [lang, setLang] = useState(() =>
    localStorage.getItem("guideLang")
  );
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  /* ✅ Pick PDF based on language */
  const pdfFile =
    lang === "te" ? "/guidetelugu.pdf" : "/guideenglish.pdf";

  /* ✅ Render PDF (UNCHANGED & WORKING) */
  useEffect(() => {
    if (!lang) return;

    localStorage.setItem("guideLang", lang);
    containerRef.current.innerHTML = "";
    setLoading(true);
    setProgress(0);

    let cancelled = false;

    const renderPdf = async () => {
      const pdf = await pdfjsLib.getDocument(pdfFile).promise;

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        if (cancelled) return;

        const page = await pdf.getPage(pageNum);

        const unscaledViewport = page.getViewport({ scale: 1 });
        const containerWidth =
          containerRef.current.clientWidth - 20;
        const scale = containerWidth / unscaledViewport.width;
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
          canvasContext: context,
          viewport,
        }).promise;

        containerRef.current.appendChild(canvas);

        setProgress(
          Math.round((pageNum / pdf.numPages) * 100)
        );
      }

      setLoading(false);
    };

    renderPdf();

    return () => {
      cancelled = true;
    };
  }, [lang, pdfFile]);

  return (
    <>
      {/* ✅ Component-scoped styles */}
      <style>
        {`
          canvas {
            display: block;
            max-width: 100%;
            height: auto;
            background: white;
            margin-bottom: 24px;
          }

          /* Toolbar buttons */
          .guide-toolbar {
            display: flex;
            justify-content: center;
            gap: 8px;
            margin: 12px 0;
          }

          .guide-toolbar button {
            padding: 6px 14px;       /* 🔽 reduced */
            border-radius: 6px;
            border: 1px solid #0a7d05;
            cursor: pointer;
            font-size: 13px;         /* 🔽 reduced */
            background: #fff;
            color: #000;
          }

          .guide-toolbar button.active {
            background: #0a7d05;
            color: #fff;
          }

          /* Initial language selection */
          .lang-select {
            min-height: 80vh;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 14px;
          }

          .lang-select h2 {
            font-size: 20px;
          }

          .lang-select button {
            padding: 8px 26px;       /* 🔽 reduced */
            font-size: 15px;         /* 🔽 reduced */
            border-radius: 8px;
            border: 1px solid #0a7d05;
            cursor: pointer;
            background: #fff;
            color: #000;
          }

          .lang-select button.active {
            background: #0a7d05;
            color: #fff;
          }
        `}
      </style>

      {/* ✅ Navbar */}
      <Navigation data={JsonData.Navigation} />

      {/* ✅ Language Selection */}
      {!lang && (
        <div className="lang-select">
          <h2>Select Guide Language</h2>

          <button
            className={lang === "en" ? "active" : ""}
            onClick={() => setLang("en")}
          >
            English
          </button>

          <button
            className={lang === "te" ? "active" : ""}
            onClick={() => setLang("te")}
          >
            తెలుగు (Telugu)
          </button>
        </div>
      )}

      {/* ✅ Toolbar */}
      {lang && (
        <div className="guide-toolbar">
          <button
            className={lang === "en" ? "active" : ""}
            onClick={() => setLang("en")}
          >
            English
          </button>

          <button
            className={lang === "te" ? "active" : ""}
            onClick={() => setLang("te")}
          >
            తెలుగు
          </button>
        </div>
      )}

      {/* ✅ Loading */}
      {loading && (
        <div style={{ textAlign: "center" }}>
          <p>Loading guide… {progress}%</p>
        </div>
      )}

      {/* ✅ PDF container */}
      <div
        ref={containerRef}
        style={{
          width: "100%",
          padding: "10px",
          display: lang ? "flex" : "none",
          flexDirection: "column",
          alignItems: "center",
        }}
      />
    </>
  );
}
